/**
 * Device Unlock API Service
 * Handles all device unlock related API calls following SOE backend patterns
 * Refactored to use centralized API management pattern
 */

import { DeviceUnlockResponse } from '../types/deviceUnlock';
import { shouldUseMockAPIs } from '../helper/apiUtils';
import { serverApiContracts } from '../common/serverApiContractMapper';
import { getApiBaseURLSSR } from '../helper/getApiBaseURLSSR';
import { postAPI, getAPI, brandMapper } from '../common/requests';
import { DEVICE_UNLOCK_CONFIG, getSoeUrl, getSoeHeaders } from '@/lib/config/deviceUnlockConfig';
import { getJWTFromHeaders } from '@/lib/utils/headerUtils';
import { log } from '@/lib/utils/logger';
import { 
  DEVICE_UNLOCK_HOME_API_CLIENT,
  DEVICE_UNLOCK_UNLOCKED_API_CLIENT,
  DEVICE_UNLOCK_INELIGIBLE_API_CLIENT,
  DEVICE_UNLOCK_ORDER_SUMMARY_API_CLIENT
} from '../common/apiContracts';
import deviceUnlockApi from '../../../lib/deviceUnlockApi';
import { adaptHomePageResponse, adaptStatusResponse } from '../../../lib/apiResponseAdapter';
import { isSpanishURL } from '@/lib/utils/brandUtils';

/**
 * Get device unlock home page data
 */
export const getDeviceUnlockHomePage = async (brand: string = 'straighttalk'): Promise<DeviceUnlockResponse> => {
  try {
    log.debug(`getDeviceUnlockHomePage - brand: ${brand}`);
    const useMockAPIs = shouldUseMockAPIs();
    if (useMockAPIs) {
      log.debug('Using mock home page API');
      if (globalThis.window === undefined && process.env.NODE_ENV !== 'test') {
        const mockModule = await import('@/mocks/responses/deviceUnlockHome.json');
        const mockData = mockModule.default as unknown as DeviceUnlockResponse;
        log.debug(`Mock home JSON loaded on server, components: ${mockData?.body?.components?.length || 0}`);
        return mockData;
      }
      // Use mock API for development
      const response = await fetch(DEVICE_UNLOCK_HOME_API_CLIENT);
      if (!response.ok) {
        throw new Error('Failed to fetch home page data');
      }
      const data = await response.json();
      log.debug(`Mock home API response received, components: ${data?.body?.components?.length || 0}`);
      log.debug(`Component types: ${JSON.stringify(data?.body?.components?.map((c: { type?: string }) => c.type) || [])}`);
      return data;
    }

    log.debug('Using real SOE backend API for home page');
    // Use real SOE backend API and adapt the response
    const realApiData = await deviceUnlockApi.getHomeContent();
    return adaptHomePageResponse(realApiData as any);
  } catch (error) {
    log.error('Error fetching device unlock home page:', error);
    throw error;
  }
};

/**
 * Get device locked status data
 */
export const getDeviceLockedStatus = async (imei: string, brand: string = 'straighttalk'): Promise<DeviceUnlockResponse> => {
  try {
    log.debug(`getDeviceLockedStatus - imei: ${imei}, brand: ${brand}`);
    log.debug('Using real SOE backend API for locked status');
    // Use real SOE backend API and adapt the response
    const realApiData = await deviceUnlockApi.getLockedPhone();
    return adaptStatusResponse(realApiData as any);
  } catch (error) {
    log.error('Error fetching device locked status:', error);
    throw error;
  }
};

/**
 * Get device unlocked status data
 */
export const getDeviceUnlockedStatus = async (imei: string, brand: string = 'straighttalk'): Promise<DeviceUnlockResponse> => {
  try {
    log.debug(`getDeviceUnlockedStatus - imei: ${imei}, brand: ${brand}`);
    const useMockAPIs = shouldUseMockAPIs();
    if (useMockAPIs) {
      log.debug('Using mock unlocked status API');
      // Use mock API for development
      const response = await fetch(DEVICE_UNLOCK_UNLOCKED_API_CLIENT);
      if (!response.ok) {
        throw new Error('Failed to fetch unlocked status data');
      }
      return response.json();
    }

    log.debug('Using real SOE backend API for unlocked status');
    // Use real SOE backend API and adapt the response
    const realApiData = await deviceUnlockApi.getUnlocked();
    return adaptStatusResponse(realApiData as any);
  } catch (error) {
    log.error('Error fetching device unlocked status:', error);
    throw error;
  }
};

/**
 * Get device eligible status data
 * For eligible devices, we navigate to /eligible page with hardcoded form fields
 * This just fetches the API response for reference/validation
 */
export const getDeviceEligibleStatus = async (imei: string, brand: string = 'straighttalk'): Promise<DeviceUnlockResponse> => {
  try {
    // Use real SOE backend API
    log.debug('Using real API');
    const realApiData = await deviceUnlockApi.getDeviceStatus();
    log.debug('Real API response:', realApiData);
    return realApiData as DeviceUnlockResponse;
  } catch (error) {
    log.error('Error fetching device eligible status:', error);
    throw error;
  }
};

/**
 * Get device ineligible status data
 */
export const getDeviceIneligibleStatus = async (imei: string, brand: string = 'straighttalk'): Promise<DeviceUnlockResponse> => {
  try {
    log.debug(`getDeviceIneligibleStatus - imei: ${imei}, brand: ${brand}`);
    const useMockAPIs = shouldUseMockAPIs();
    if (useMockAPIs) {
      log.debug('Using mock ineligible status API');
      // Use mock API for development
      const response = await fetch(DEVICE_UNLOCK_INELIGIBLE_API_CLIENT);
      if (!response.ok) {
        throw new Error('Failed to fetch ineligible status data');
      }
      return response.json();
    }

    log.debug('Using real SOE backend API for ineligible status');
    // Use real SOE backend API and adapt the response
    const realApiData = await deviceUnlockApi.getIneligible();
    return adaptStatusResponse(realApiData as any);
  } catch (error) {
    log.error('Error fetching device ineligible status:', error);
    throw error;
  }
};

/**
 * Get device pending status data
 */
export const getDevicePendingStatus = async (imei: string, brand: string = 'straighttalk'): Promise<DeviceUnlockResponse> => {
  try {
    log.debug(`getDevicePendingStatus - imei: ${imei}, brand: ${brand}`);
    const useMockAPIs = shouldUseMockAPIs();
    if (useMockAPIs) {
      log.debug('Using mock pending status API');
      // Use mock API for development
      const response = await fetch('/api/mock/device-unlock/pending');
      if (!response.ok) {
        throw new Error('Failed to fetch pending status data');
      }
      return response.json();
    }

    log.debug('Using real SOE backend API for pending status');
    // Use real SOE backend API and adapt the response
    const realApiData = await deviceUnlockApi.getDevicePending();
    return adaptStatusResponse(realApiData as any);
  } catch (error) {
    log.error('Error fetching device pending status:', error);
    throw error;
  }
};

/**
 * Get device delayed status data
 */
export const getDeviceDelayedStatus = async (imei: string, brand: string = 'straighttalk'): Promise<DeviceUnlockResponse> => {
  try {
    log.debug(`getDeviceDelayedStatus - imei: ${imei}, brand: ${brand}`);
    const useMockAPIs = shouldUseMockAPIs();
    if (useMockAPIs) {
      log.debug('Using mock delayed status API');
      // Use mock API for development
      const response = await fetch('/api/mock/device-unlock/delayed');
      if (!response.ok) {
        throw new Error('Failed to fetch delayed status data');
      }
      return response.json();
    }

    log.debug('Using real SOE backend API for delayed status');
    // Use real SOE backend API and adapt the response
    const realApiData = await deviceUnlockApi.getDelayed();
    return adaptStatusResponse(realApiData as any);
  } catch (error) {
    log.error('Error fetching device delayed status:', error);
    throw error;
  }
};

/**
 * Get device phone not activated status data
 */
export const getDevicePhoneNotActivatedStatus = async (imei: string, brand: string = 'straighttalk'): Promise<DeviceUnlockResponse> => {
  try {
    log.debug(`getDevicePhoneNotActivatedStatus - imei: ${imei}, brand: ${brand}`);
    const useMockAPIs = shouldUseMockAPIs();
    if (useMockAPIs) {
      log.debug('Using mock phone not activated status API');
      // Use mock API for development
      const response = await fetch('/api/mock/device-unlock/phone-not-activated');
      if (!response.ok) {
        throw new Error('Failed to fetch phone not activated status data');
      }
      return response.json();
    }

    log.debug('Using real SOE backend API for phone not activated status');
    // Use real SOE backend API and adapt the response
    const realApiData = await deviceUnlockApi.getPhoneNotActivated();
    return adaptStatusResponse(realApiData as any);
  } catch (error) {
    log.error('Error fetching device phone not activated status:', error);
    throw error;
  }
};
/**
 * Get device phone not activated status data
 */
export const getDeviceOrderSummary = async (): Promise<DeviceUnlockResponse> => {
  try {
    const useMockAPIs = shouldUseMockAPIs();
    if (useMockAPIs) {
      // Use mock API for development
      const response = await fetch('/api/mock/device-unlock/order-summary');
      if (!response.ok) {
        throw new Error('Failed to fetch order summary data');
      }
      return response.json();
    }

    log.debug('Using real SOE backend API for order summary');
    // Use real SOE backend API and adapt the response
    const realApiData = await deviceUnlockApi.getDeviceUnlockOrderSummary();
    return adaptStatusResponse(realApiData as any);
  } catch (error) {
    log.error('Error fetching device order summary:', error);
    throw error;
  }
};
/**
 * Submit device unlock request
 */
export const submitDeviceUnlockRequest = async (data: {
  imei: string;
  email: string;
  phone: string;
  firstName: string;
  lastName: string;
  brand: string;
}): Promise<DeviceUnlockResponse> => {
  try {
    const useMockAPIs = shouldUseMockAPIs();
    if (useMockAPIs) {
      // Use mock API for development
      const response = await fetch(DEVICE_UNLOCK_ORDER_SUMMARY_API_CLIENT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      if (!response.ok) {
        throw new Error('Failed to submit unlock request');
      }
      return response.json();
    }

    // Use real SOE backend for production
    const url = serverApiContracts['deviceUnlockSubmit']()[data.brand];
    const baseApiUrl = await getApiBaseURLSSR();
    const fullUrl = `${baseApiUrl}${url}`;
    
    return await postAPI(fullUrl, data, data.brand);
  } catch (error) {
    log.error('Error submitting device unlock request:', error);
    throw error;
  }
};

/**
 * Check device unlock eligibility with SOE backend
 * @param data - Object containing imei, brand, and optional additionalHeaders from Imperva
 * @param additionalHeaders - Headers extracted from incoming request (includes JWT from Imperva)
 * @returns Device unlock eligibility response
 */
export const checkDeviceUnlockEligibility = async (
  data: {
    imei: string;
    brand: string;
    captchaToken: string;
    domainName?: string;
  },
  additionalHeaders: Record<string, string> = {}
): Promise<DeviceUnlockResponse> => {
  try {
    // Construct the actual SOE endpoint URL using centralized config
    const fullUrl = `${getSoeUrl('ELIGIBILITY', data.domainName)}?${new URLSearchParams(DEVICE_UNLOCK_CONFIG.ELIGIBILITY_QUERY_PARAMS).toString()}&identifier=${data.imei.trim()}`;
    
    // Map brand to SOE brand format (convert to uppercase)
    const brandUpper = brandMapper?.[data.brand] || data.brand.toUpperCase();
    
    // Get JWT from Imperva-injected headers
    const jwtToken = getJWTFromHeaders(additionalHeaders);
    
    const headers = getSoeHeaders({
      'brand': brandUpper,
      'pspct-jwt': jwtToken,
      'accept-language': isSpanishURL() ? 'SPA' : 'ENG', // Can be dynamic based on user preference
      ...additionalHeaders
    });
    if(data?.captchaToken?.trim() !== 'dev_bypass_token') {
      log.warn(`Missing captcha token - IMEI: ${data.imei}, Brand: ${data.brand}, captchaToken: ${data.captchaToken?.toString().slice(0, 10)}`);
      headers['recaptchaenterprise'] = data.captchaToken;
      headers['recaptchaflowtype'] = 'login';
    }

    log.debug('Checking device eligibility - URL:', fullUrl);
    log.debug('Headers:', { ...headers, 'pspct-jwt': jwtToken ? 'PROVIDED' : 'MISSING', 'recaptchaenterprise': data.captchaToken !=='dev_bypass_token' ? 'PROVIDED' : 'MISSING' });
    
    const response = await fetch(fullUrl, {
      method: 'GET',
      headers
    });

    if (!response.ok) {
      const errorBody = await response.text();
      log.error(`Eligibility check failed: ${response.status} ${response.statusText}`, errorBody);
      throw new Error(`Eligibility check failed: ${response.statusText}`);
    }

    const result = await response.json();
    log.debug('Eligibility response received:', result);
    return result;
  } catch (error) {
    log.error('Error checking device unlock eligibility:', error);
    throw error;
  }
};

/**
 * Health check for SOE service
 */
export const checkSOEHealth = async (brand: string = 'straighttalk'): Promise<{ healthy: boolean }> => {
  try {
    const url = serverApiContracts['soeHealthCheck']()[brand];
    const baseApiUrl = await getApiBaseURLSSR();
    const fullUrl = `${baseApiUrl}${url}`;
    
    const response = await getAPI(fullUrl, brand);
    return { healthy: response.status === 'healthy' };
  } catch (error) {
    log.error('Error checking SOE health:', error);
    return { healthy: false };
  }
};

/**
 * Submit device unlock case creation request
 * Called when user submits the eligible form
 */
export const submitDeviceUnlockCase = async (
  data: {
    imei: string;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    brand: string;
    domainName?: string;
  },
  additionalHeaders: Record<string, string> = {}
): Promise<any> => {
  try {
    // Construct the actual SOE endpoint URL using centralized config
    const fullUrl = getSoeUrl('CASE_CREATION', data.domainName);
    
    // Map brand to SOE brand format (convert to uppercase)
    const brandUpper = brandMapper?.[data.brand] || data.brand.toUpperCase();
    
    // Get JWT from Imperva-injected headers
    const jwtToken = getJWTFromHeaders(additionalHeaders);
    
    const headers = getSoeHeaders({
      'brand': brandUpper,
      'pspct-jwt': jwtToken,
      'accept-language': isSpanishURL() ? 'SPA' : 'ENG', // Can be dynamic based on user preference
      ...additionalHeaders
    });
    
    // Build request payload
    const payload = {
      data: {
        relatedParties: [
          {
            party: {
              partyID: 'TWWEB',
              languageAbility: isSpanishURL() ? 'SPA' : 'ENG',
              partyExtension: [
                {
                  name: 'sourceSystem',
                  value: 'DOTCOM'
                }
              ]
            },
            roleType: 'internal'
          }
        ],
        customer: {
          firstName: data.firstName,
          lastName: data.lastName,
          contactMedium: [
            {
              mediumType: 'emailAddress',
              contactDetails: {
                emailAddress: data.email
              }
            },
            {
              mediumType: 'phoneNumber',
              contactDetails: {
                phoneNumber: data.phoneNumber
              }
            }
          ],
          products: [
            {
              productSerialNumber: data.imei,
              mdn: '' // Can be populated if needed
            }
          ]
        }
      }
    };
    
    log.debug('Submitting device unlock case - URL:', fullUrl);
    log.debug('Headers:', { ...headers, 'pspct-jwt': jwtToken ? 'PROVIDED' : 'MISSING' });
    log.debug('Payload:', payload);
    
    const response = await fetch(fullUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const errorBody = await response.text();
      log.error(`Case creation failed: ${response.status} ${response.statusText}`, errorBody);
      throw new Error(`Case creation failed: ${response.statusText}`);
    }

    const result = await response.json();
    log.debug('Case creation response received:', result);
    return result;
  } catch (error) {
    log.error('Error submitting device unlock case:', error);
    throw error;
  }
};